import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, Users, ArrowLeftRight, CalendarClock, Plus, UserPlus, Target, LogIn, ArrowRight, ChevronRight } from "lucide-react";
import { api, money, fmtDate, relTime } from "../lib/api";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { StatCard, Avatar, Spinner, EmptyState } from "../components/bits";
import { GroupCard } from "../components/GroupCard";
import { RecordPaymentDialog } from "../components/dialogs/RecordPaymentDialog";
import { JoinGroupDialog } from "../components/dialogs/JoinGroupDialog";
import { GoalDialog } from "../components/dialogs/GoalDialog";
import { BalanceCard } from "../components/BalanceCard";

function NewsStrip() {
  const [news, setNews] = useState([]);
  useEffect(() => { api.get("/news", { params: { limit: 3 } }).then((r) => setNews(r.data)).catch(() => {}); }, []);
  if (!news.length) return null;
  return <div className="mb-4 space-y-2" data-testid="news-strip">{news.map((n) => <Link key={n.id} to={`/app/groups/${n.org_id}?tab=community`} className="block rounded-xl bg-nini-mint/50 border border-nini-border/60 p-3 hover:bg-nini-mint transition" data-testid={`news-strip-${n.id}`}><div className="text-[10px] font-bold tracking-wider text-nini-deep uppercase">{n.group_name} · {n.category.replace("_", " ")}</div><div className="text-sm font-semibold text-nini-heading">{n.title}</div><div className="text-xs text-nini-muted line-clamp-2">{n.body}</div></Link>)}</div>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [payReq, setPayReq] = useState(null);
  const [join, setJoin] = useState(false);
  const [goal, setGoal] = useState(false);

  const load = () => Promise.all([api.get("/dashboard"), api.get("/groups")]).then(([d, g]) => { setData(d.data); setGroups(g.data); });
  useEffect(() => { load(); }, []);
  if (!data) return <Spinner />;

  const actions = [
    ["Create group", "Start a stokvel or savings group", Plus, () => nav("/app/groups/new"), "qa-create-group"],
    ["Join group", "Enter an invitation code", LogIn, () => setJoin(true), "qa-join-group"],
    ["Record contribution", "Mark a payment as made", ArrowLeftRight, () => nav("/app/pay"), "qa-record-contribution"],
    ["Create savings goal", "Save for what matters", Target, () => setGoal(true), "qa-create-goal"],
    ["Invite member", "Grow one of your groups", UserPlus, () => nav(groups[0] ? `/app/groups/${groups[0].id}?tab=members&invite=1` : "/app/groups"), "qa-invite-member"],
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-nini-heading tracking-tight">{t("dashboard.welcome", { name: user.first_name })}</h1>
          <p className="text-sm text-nini-muted mt-1">Here's an overview of your groups and recorded finances.</p>
        </div>
        <Link to="/app/groups/new" className="btn-primary" data-testid="dashboard-create-group-btn"><Plus size={16} /> {t("nav.createGroup")}</Link>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4"><BalanceCard onChanged={load} /></div>
        <div className="lg:col-span-8 grid grid-cols-2 gap-4 md:gap-6 stagger">
          <StatCard label={t("dashboard.recordedBalance")} value={money(data.recorded_balance)} hint={t("dashboard.acrossGroups")} icon={Wallet} testId="stat-recorded-balance" />
          <StatCard label={t("dashboard.activeGroups")} value={data.active_groups} hint={t("dashboard.belongTo")} icon={Users} testId="stat-active-groups" />
          <StatCard label={t("dashboard.totalContributions")} value={money(data.my_total_contributions)} hint={t("dashboard.verifiedByYou")} icon={ArrowLeftRight} testId="stat-total-contributions" />
          <StatCard label={t("dashboard.upcoming")} value={money(data.upcoming_amount)} hint={t("dashboard.due", { count: data.upcoming_count })} icon={CalendarClock} testId="stat-upcoming-payments" />
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <section>
            <div className="flex items-center justify-between mb-4"><h2 className="text-base md:text-lg font-semibold text-nini-heading">{t("dashboard.myGroups")}</h2><Link to="/app/groups" className="text-sm font-medium text-nini-deep inline-flex items-center gap-1">{t("common.seeAll")} <ArrowRight size={14} /></Link></div>
            {groups.length === 0 ? <EmptyState title="No groups yet" hint="Create your first group or join one with an invitation code." action={<Link to="/app/groups/new" className="btn-primary">Create group</Link>} /> : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 stagger">{groups.slice(0, 3).map((g) => <GroupCard key={g.id} g={g} />)}</div>
            )}
          </section>

          <section className="card p-5 md:p-6">
            <h2 className="text-base md:text-lg font-semibold text-nini-heading mb-4">{t("dashboard.quickActions")}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {actions.map(([t, d, Icon, fn, id]) => (
                <button key={t} onClick={fn} data-testid={id} className="group flex items-center gap-3 p-4 rounded-xl bg-nini-surface/70 hover:bg-nini-mint transition text-left">
                  <span className="h-10 w-10 rounded-xl bg-white text-nini-deep inline-flex items-center justify-center shadow-soft"><Icon size={18} /></span>
                  <span className="flex-1 min-w-0"><span className="block text-sm font-semibold text-nini-heading">{t}</span><span className="block text-xs text-nini-muted truncate">{d}</span></span>
                  <ChevronRight size={16} className="text-nini-muted group-hover:translate-x-0.5 transition" />
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <section className="card p-5 md:p-6" data-testid="upcoming-panel">
            <h2 className="text-base font-semibold text-nini-heading mb-4">Upcoming payments</h2>
            {data.upcoming.length === 0 ? <p className="text-sm text-nini-muted">You're all caught up.</p> : data.upcoming.map((u) => (
              <button key={u.id} onClick={() => setPayReq(u.id)} data-testid={`upcoming-item-${u.id}`} className="w-full flex items-center gap-3 py-3 border-b last:border-0 border-nini-border text-left hover:bg-nini-surface/60 -mx-2 px-2 rounded-lg transition">
                <span className={`h-2 w-2 rounded-full ${u.status === "overdue" ? "bg-[#C53030]" : "bg-nini-sage"}`} />
                <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-nini-heading truncate">{u.ref_name}</span><span className="block text-xs text-nini-muted">{u.group_name} · due {fmtDate(u.due_date)}</span></span>
                <span className="text-sm font-semibold text-nini-heading">{money(u.amount - (u.paid_amount || 0))}</span>
              </button>
            ))}
          </section>
          <section className="card p-5 md:p-6" data-testid="recent-activity-panel">
            <div className="flex items-center justify-between mb-4"><h2 className="text-base font-semibold text-nini-heading">{t("dashboard.recentActivity")}</h2><Link to="/app/activity" className="text-sm font-medium text-nini-deep">{t("common.viewAll")}</Link></div>
            <NewsStrip />
            <div className="space-y-1">
              {data.activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3 py-2.5">
                  <Avatar src={a.actor_avatar} name={a.actor_name} size="h-8 w-8" />
                  <div className="min-w-0 flex-1"><p className="text-sm text-nini-text leading-snug">{a.message}</p><span className="text-[11px] text-nini-muted">{a.group_name} · {relTime(a.created_at)}</span></div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <RecordPaymentDialog requestId={payReq} onClose={() => setPayReq(null)} onDone={load} />
      <JoinGroupDialog open={join} onClose={() => setJoin(false)} onDone={load} />
      <GoalDialog open={goal} onClose={() => setGoal(false)} onDone={() => nav("/app/goals")} />
    </div>
  );
}
